import { IJourneyCopy } from './copyTypes';

/**
 * The wording every partner sees today. Changing anything here changes the
 * journey for everyone, so treat it as the existing behaviour it is.
 */
export const DEFAULT_COPY: IJourneyCopy = {
    quickCalculator: {
        showProgressBar: true,
        groupFields: false,
        darkEstimate: false,
        sloganLines: ['Get an instant response.', 'No Impact on credit score.'],
        estimateLabel: 'Estimated Quote',
        estimateHeading: 'You can finance this from only*',
        showEstimateFootnote: true,
        disclaimerOnHeading: false,
        cta: 'Get Personalised Quote',
    },
    progressSteps: [
        'Payment\nEstimate',
        'Your\nDetails',
        'Your\nQuote',
        'Apply\nto lender',
    ],
    progressStepMap: [0, 1, 2, 3],
    screens: {
        // The loan screen has never shown a heading; an empty title keeps it
        // that way for every partner that has not asked for one.
        loan: { title: '' },
        business: {
            title: 'Business details',
            subtitle: 'We need some details about your business',
        },
        basicDetails: {
            title: 'Basic details',
            subtitle: 'We just need some basic details about you.',
        },
        income: {
            title: 'Income details',
            subtitle:
                'This section looks at your source of income and employment.',
        },
        identification: {
            title: 'Identification',
            subtitle:
                "Use your driver's licence to pre-fill the rest of your application for a faster, effortless experience.",
        },
        ocrSummary: {
            title: 'Details summary',
            subtitle:
                "Here's what we've got so far. Something missing or not quite right? Select the box or the Edit button to fix it up.",
        },
        employment: {
            title: 'Work details',
            subtitle: 'We need your last three years of employment history.',
        },
        assetsAndLiabilities: {
            title: 'Assets and liabilities',
            subtitle: 'Tell us what you own and what you owe.',
        },
        supportingDocs: {
            title: 'Supporting documents',
            subtitle:
                'These are the documents your selected lender needs from you.',
        },
    },
    fields: {
        purchasePrice: "What's the purchase price?",
        loanTerm: "What's your preferred loan term?",
        loanTermMinUnit: 'year',
        loanTermMaxUnit: 'years',
        vehicleCondition: 'Is the {asset} new or used?',
        creditHistory: "What's your credit history like?",
        loanPurpose: "What's the purpose of the loan?",
        tradeIn: 'Trade-in',
        deposit: 'Deposit',
        balloon: 'Balloon percentage',
        vehicleKnown: 'Do you know the {asset} you want?',
        vehicleKnownOptions: { yes: 'Yes', no: 'Not Yet' },

        businessIndustry: 'Industry type',
        abnLookup: 'Use ABN lookup?',

        livingStatus: 'Living status',
        livingStatusOptions: {
            owner: 'Property Owner',
            renter: 'Renter',
            boarding: 'Boarding / Other',
        },
        marriageStatus: 'Marriage status',
        marriageStatusOptions: {
            married: 'Married / De Facto',
            single: 'Single / Divorced',
        },
        residencyStatusOptions: {
            citizen: 'Citizen',
            permanentResident: 'Permanent Resident',
            visa: 'Visa',
        },
        haveKids: 'Do you have kids?',
        haveKidsOptions: { yes: 'Yes', no: 'No' },
        numberOfKids: 'Number of kids',

        paidFrequency: 'I get paid',
        housingFrequency: 'How often do you pay your rent or mortgage?',
        otherIncome: 'Any other income',

        licenceFront: "Driver's License Front (Card Only)",
        licenceBack: "Driver's License Back (Card Only)",
        skipIdentification: "I'll do it later",

        assetCards: {
            realEstate: 'Real Estate',
            motorVehicle: 'Motor Vehicle',
            savings: 'Savings and More',
            creditCards: 'Credit Cards',
            personalLoans: 'Personal Loans',
        },

        backButton: 'Back',
        nextButton: 'Next',

        submitApplication: 'Submit',

        termsConsentLead: 'I have read & agreed to the',
        termsConsentLink: 'Terms & Conditions',
        // Character for character what the hardcoded original sent, so every
        // partner without its own wording is unaffected.
        termsSmsMessage:
            'Thankyou for agreeing to the terms and conditions found here \n\n{link}',

        // Undefined keeps the asset-specific titles: Let's Go Driving,
        // Riding, Camping, Get Started.
        registerTitle: undefined,
        registerSubtitle: [
            'We will send you a one time passcode.',
            'No spam, promise.',
        ],
    },
    checkingApproval: { title: '', body: [] },
    showScoresScreen: true,
    financeOptionCard: false,
    sentenceCaseAssetHeading: false,
    quietFallbackPrompt: false,
    leftAlignHeading: false,
    segmentedProgress: false,
    plainSubmittedModal: false,
    cardListRows: false,
    cardGround: false,
    segmentedOptions: false,
    referredTimeline: false,
    referredDetail: {
        intro: '',
        body: '',
        steps: [],
        contactText: '',
        callCta: 'Call',
        backToStart: 'Back to start',
    },
    declinedCard: false,
    successCard: false,
    failureCard: false,
    technicalFailureScreen: false,
    technicalFailure: {
        title: '',
        bodyPrefix: '',
        bodySuffix: '',
        cta: 'Try again',
        backToStart: 'Back to start',
    },
    contact: {
        heading: 'Have a question?\nGet in touch:',
        reduceLoan: 'Reduce Loan',
        editAnswers: 'Edit Answers',
        editIncome: 'Edit Income Details',
    },
    submitLabelWhenSubmitting: false,
    declinedDetail: {
        contactText: '',
        callCta: 'Call',
        backToStart: 'Back to start',
    },
    applicationSubmitted: { title: '', body: [], cta: 'Close' },
    financeOptions: {
        title: '',
        body: [],
        lowestBadge: 'Lowest Repayment',
        estimateLabel: 'Estimated repayment',
        advertisedLabel: 'Advertised rate',
        comparisonLabel: 'Comparison rate',
        loanTermLabel: 'Loan term',
        upfrontFeeLabel: 'Upfront fee',
        ongoingFeeLabel: 'Ongoing fee',
        balloonLabel: 'Balloon payment',
        cta: 'Choose Lender',
    },
    outcomes: {
        servicingSuccess: {
            title: 'Great News!',
            body: [],
        },
        servicingFailure: {
            // The \n are the line breaks the original markup forced with
            // <br />. Kept so this screen is unchanged for partners who have
            // not asked for new wording.
            title: 'Check your income\nand expenses',
            body: [
                "It looks like your income might not be high \nenough for the amount you'd like to borrow",
                "Check your income and expenses to make sure \nthey're correct or consider reducing the amount \nyou'd like to borrow.",
            ],
        },
        noIncome: {
            title: 'Regular income is needed',
            body: [
                'Every lender we work with needs to see a regular income before they can consider an application.',
                'That includes wages, and with some lenders, government payments such as a pension or disability support.',
                'If that has changed, or this was picked by mistake, you can go back and update your answer.',
            ],
        },
        referred: {
            title: "We'll be in touch soon!",
            body: [
                'Our friendly team will get in touch with \nyou to finalise the application.',
            ],
        },
        declined: {
            title: 'Sorry about that',
            body: ["Unfortunately, we can't help you right now."],
        },
    },
};
